import React, { useMemo } from "react";
import styled from "styled-components";
import profilePreferences from "@coworker/enums/profilePreferences";
import { useTranslation, Trans } from "@coworker/locales";
import {
  searchMFAQ,
  useGetTopQuestions,
  useGetOtherQuestions,
} from "./hooks/useMFAQsService";
import { describeAreaFilter } from "../InputPopup/UserFiltersRow/filterChoices";
import useFixaNavigate from "@coworker/apprestructured/src/shared/wrappers/FixaNavigate/useFixaNavigate";
import Instructions from "../Instructions";
import { PositionedLoaderIcon } from "@coworker/reusable/Loader";
import { useStoreId } from "../../core/auth/useLoggedInUser";
import Questions from "./Questions";
import EmptyStateScreen from "../EmptyStateScreen";
import Button, { WideButton } from "@coworker/components/src/components/Button";
import FixaSearch from "@coworker/apprestructured/src/shared/wrappers/FixaSearch/FixaSearch";
import Linkbar from "@coworker/apprestructured/src/shared/components/Linkbar/Linkbar";
import { useMfaqFilters } from "./hooks/useMfaqFilters";
import { FilterPanel } from "@coworker/apprestructured/src/shared/filters/components/FilterPanel";
import { getPeriodTimestampsFromFilters } from "./helper";
import { DEFUALT_PERIOD_VALUE, PERIOD_ID } from "./hooks/usePeriodFilter";
import { DEFUALT_LOCATION_VALUE, LOCATION_ID } from "./hooks/useLocationFilter";
import {
  DEFAULT_QUESTIONS_WITH_TASKS_ONLY_VALUE,
  QUESTIONS_WITH_TASKS_ONLY_ID,
} from "./hooks/useQuestionsWithTasksOnlyFilter";
import { getSelectedFilterValue } from "@coworker/apprestructured/src/shared/filters/utils/filter/filtervalue";
import { MFAQ_LABEL } from "./constants";

const { DISABLE_POPUPS_MFAQ_SELECT_AREA } = profilePreferences;

const ScrollContainer = styled("div")`
  flex-grow: 1;
  overflow-y: auto;
  padding-bottom: 68px;
`;

const ButtonContainer = styled("div")`
  width: 15rem;
  margin: auto;
`;

const SectionHeader = styled("div")`
  font-weight: bold;
  font-size: 18px;
  height: 60px;
  line-height: 60px;
  padding: 0 24px;

  white-space: nowrap;
  overflow-x: auto;
  -webkit-scrollbar-width: none;
  -moz-scrollbar-width: none;
  -ms-scrollbar-width: none;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }

  -ms-overflow-style: none;
`;

const ActionBar2 = styled("div")`
  position: fixed;
  display: flex;
  justify-content: center;
  width: 100%;
  padding-block: 10px;
  background-color: white;
  border-top: 1px solid var(--grey200);
  bottom: 0;
  @media (max-width: 1200px) {
    bottom: 68px;
  }
`;

const ListSectionHeader = styled(SectionHeader)`
  background-color: var(--grey100);
  padding-top: 8px;
`;

const StyledButton = styled(Button)`
  width: 90%;
`;

const SearchWrapper = styled("div")`
  padding: 12px;
  border-bottom: 1px solid var(--grey200);
`;

const TOP_N = 10;

const CustomerMFAQ = () => {
  const navigate = useFixaNavigate();
  const mfaqFilters = useMfaqFilters({ hideQuestionsWithTasksOnly: false });

  const period = getSelectedFilterValue(
    PERIOD_ID,
    mfaqFilters.filterValues,
    DEFUALT_PERIOD_VALUE
  ).value;

  const location = getSelectedFilterValue(
    LOCATION_ID,
    mfaqFilters.filterValues,
    DEFUALT_LOCATION_VALUE
  ).value;

  const onlyQuestionsWithTasks = getSelectedFilterValue(
    QUESTIONS_WITH_TASKS_ONLY_ID,
    mfaqFilters.filterValues,
    DEFAULT_QUESTIONS_WITH_TASKS_ONLY_VALUE
  ).value;

  const filters = useMemo(() => {
    return [period, location];
  }, [period, location]);

  const createNew = React.useCallback(
    () => navigate("/customerquestions/new"),
    [navigate]
  );

  const [startDate, endDate] = useMemo(() => {
    return getPeriodTimestampsFromFilters(mfaqFilters.filterValues);
  }, [mfaqFilters.filterValues]);

  const isCustomPeriod = period === "period=custom";
  const endDateObj = new Date(endDate);
  const today = new Date();
  const isEndDateToday =
    endDateObj.getDate() !== today.getDate() ||
    endDateObj.getMonth() !== today.getMonth() ||
    endDateObj.getFullYear() !== today.getFullYear();

  const canVoteInPast = !isCustomPeriod
    ? true
    : isCustomPeriod && !isEndDateToday;

  const [query, setQuery] = React.useState("");
  const [foundQuestions, setFoundQuestions] = React.useState([]);

  const isWholeStore = location === DEFUALT_LOCATION_VALUE.value;
  const store_id = useStoreId();

  const [from, setFrom] = React.useState(TOP_N);
  const [isOtherQuestionsLoaded, setIsOtherQuestionsLoaded] =
    React.useState(false);

  const [shouldShowLoadMoreButton, setShouldShowLoadMoreButton] =
    React.useState(true);

  const { data: otherQuestions } = useGetOtherQuestions(
    startDate,
    endDate,
    location,
    from,
    onlyQuestionsWithTasks
  );
  const [questionsList, setQuestionsList] = React.useState([]);
  const prevFrom = React.useRef(from);
  const prevFirstProductId = React.useRef(
    (otherQuestions ?? [])[0]?.question_id
  );
  React.useEffect(() => {
    if (
      otherQuestions == null ||
      !Array.isArray(otherQuestions) ||
      otherQuestions.length === 0
    )
      return;

    const isPagination = prevFrom.current !== from && from !== 0;

    const pagination = () => {
      setQuestionsList((current) => current.concat(otherQuestions));
      setShouldShowLoadMoreButton(otherQuestions.length >= TOP_N);
      prevFrom.current = from;
      prevFirstProductId.current = otherQuestions[0]?.question_id;
    };

    const updatedFilters = () => {
      setIsOtherQuestionsLoaded(false);
      setQuestionsList(otherQuestions);
      prevFrom.current = 0;
      setFrom(TOP_N);
    };

    if (isPagination) {
      pagination();
    } else {
      updatedFilters();
    }

    return () => {};
  }, [otherQuestions, from]); // Add `from` as a dependency as well.

  const { data: top10Questions, loading: isTop10Loading } = useGetTopQuestions(
    startDate,
    endDate,
    location,
    onlyQuestionsWithTasks
  );
  const scrollContainerRef = React.useRef();

  const searchWithNoHits = query && !foundQuestions?.length;
  const showLoaderIcon = !searchWithNoHits && isTop10Loading;
  const noQuestionsToList =
    top10Questions?.length === 0 && foundQuestions?.length === 0;

  const [showSearch, setShowSearch] = React.useState(false);
  const { t } = useTranslation();

  return (
    <>
      <Linkbar currentLabel={MFAQ_LABEL} currentAsH1 />
      <FilterPanel
        filterValues={mfaqFilters.filterValues}
        setFilterValues={mfaqFilters.setFilterValues}
        useFiltersFactory={mfaqFilters.useFiltersFactory}
        feature="mfaq"
      />
      <SearchWrapper>
        <FixaSearch
          id="search"
          onChange={async (e) => {
            setQuery(e.target.value);
            const found = await searchMFAQ(
              e.target.value,
              null,
              location,
              store_id
            );
            setFoundQuestions(found);
          }}
          placeholder={t("searchQuestionString")}
          onClear={() => {
            setQuery("");
            setFoundQuestions([]);
            setShowSearch(false);
          }}
        />
      </SearchWrapper>
      {searchWithNoHits && <NoQuestionsFor query={query} />}
      {showLoaderIcon && <PositionedLoaderIcon />}
      {!showLoaderIcon && (
        <ScrollContainer ref={scrollContainerRef}>
          {isWholeStore && (
            <Instructions
              title={<Trans>mfaqSelectCorrectLocationString</Trans>}
              text={<Trans>mfaqSelectLocationInstructionsString</Trans>}
              type={DISABLE_POPUPS_MFAQ_SELECT_AREA}
              showBottomBorder
            />
          )}
          {noQuestionsToList && !showSearch && (
            <EmptyStateScreen
              titleKey="noQuestionFound"
              subtitleKey="tapOnTheButtonToAddQuestion"
            />
          )}
          {!query && !!top10Questions?.length && (
            <>
              <ListSectionHeader>
                <Trans>top10String</Trans>
                <DashText filters={filters} />
              </ListSectionHeader>
              <Questions
                questions={top10Questions}
                shouldShowLoadMore={false}
                canUpvote={!isWholeStore && canVoteInPast}
                showUpvote={!isWholeStore}
              />
              {!isOtherQuestionsLoaded && top10Questions?.length === TOP_N && (
                <ButtonContainer>
                  <WideButton
                    data-testid="loadOtherQuestions"
                    secondary
                    dark
                    text={<Trans>loadMore</Trans>}
                    onClick={() => setIsOtherQuestionsLoaded(true)}
                  />
                </ButtonContainer>
              )}
            </>
          )}
          {!query && isOtherQuestionsLoaded && (
            <>
              <ListSectionHeader>
                <Trans>otherString</Trans>
                <DashText filters={filters} />
              </ListSectionHeader>
              <Questions
                questions={questionsList}
                updateFrom={() => setFrom(from + TOP_N)}
                shouldShowLoadMore={shouldShowLoadMoreButton}
                canUpvote={!isWholeStore}
                showUpvote={!isWholeStore}
              />
            </>
          )}
          {query.length > 2 && (
            <Questions
              questions={foundQuestions}
              shouldShowLoadMore={false}
              canUpvote={!isWholeStore && canVoteInPast}
              showUpvote={!isWholeStore}
              key="found"
            />
          )}
          <ActionBar2>
            <StyledButton
              data-testid="createNewMFAQ"
              primary
              dark
              text={<Trans>addNewQuestionString</Trans>}
              onClick={createNew}
            />
          </ActionBar2>
        </ScrollContainer>
      )}
    </>
  );
};

export default CustomerMFAQ;

function DashText({ filters }) {
  const area = describeAreaFilter(filters);
  return area ? (
    <span>
      <span>{" – "}</span>
      <span>{area}</span>
    </span>
  ) : (
    <span />
  );
}

const EmptyScreen = styled("div")`
  background: var(--grey100);
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  height: calc(100% - 250px); // 100% - (header + filters)

  & > div {
    margin: auto;
    font-size: 14px;
    font-weight: bold;
  }

  & > a {
    text-decoration: underline;
    font-size: 14px;
    height: 50px;
    margin: 0 auto;
    margin-bottom: 60px;
  }
`;

function NoQuestionsFor({ query }) {
  const { t } = useTranslation();
  return (
    <EmptyScreen>
      <div>{t("noQuestionsForString", { query })}</div>
    </EmptyScreen>
  );
}
